import { cn } from '@/shared/utils';
import type { DateFieldProps } from '@/view/shared/form/DateField.props';
import {
  Description,
  FieldError,
  Calendar as HeroCalendar,
  DateField as HeroDateField,
  DatePicker as HeroDatePicker,
  Label,
} from '@heroui/react';
import { CalendarDays, ChevronLeft, ChevronRight } from 'lucide-react';

export function DateField({
  label,
  value,
  onChange,
  minValue,
  maxValue,
  name,
  hasError = false,
  isRequired = false,
  isDisabled = false,
  isReadOnly = false,
  errorMessage,
  description,
  labelSuffix,
  classNames,
}: Readonly<DateFieldProps>) {
  return (
    <HeroDatePicker
      value={value ?? null}
      onChange={(next) => onChange?.(next)}
      minValue={minValue ?? null}
      maxValue={maxValue ?? null}
      isRequired={isRequired}
      isDisabled={isDisabled}
      isReadOnly={isReadOnly}
      isInvalid={hasError}
      name={name ?? ''}
      className={cn('flex w-full flex-col gap-1', classNames?.container)}
    >
      <Label className={cn('text-caption text-foreground', classNames?.label)}>
        {label}
        {labelSuffix && (
          <span className="ml-1 text-xs font-normal text-muted-foreground">{labelSuffix}</span>
        )}
      </Label>

      <HeroDateField.Group
        fullWidth
        className={cn(
          'h-8 sm:h-9 rounded-lg border border-input bg-card transition-all',
          'focus-within:border-primary focus-within:ring-2 focus-within:ring-ring',
          hasError && 'border-destructive focus-within:ring-destructive/30',
          classNames?.input,
        )}
      >
        <HeroDateField.Input className="text-xs text-foreground">
          {(segment) => <HeroDateField.Segment segment={segment} />}
        </HeroDateField.Input>

        <HeroDateField.Suffix>
          <HeroDatePicker.Trigger className="flex h-full w-8 shrink-0 cursor-pointer items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-surface-secondary hover:text-foreground">
            <HeroDatePicker.TriggerIndicator>
              <CalendarDays className="h-4 w-4" />
            </HeroDatePicker.TriggerIndicator>
          </HeroDatePicker.Trigger>
        </HeroDateField.Suffix>
      </HeroDateField.Group>

      <HeroDatePicker.Popover className="[--trigger-width:auto] z-50">
        <HeroCalendar minValue={minValue ?? null} maxValue={maxValue ?? null}>
          <HeroCalendar.Header>
            <HeroCalendar.NavButton slot="previous">
              <ChevronLeft className="h-4 w-4" />
            </HeroCalendar.NavButton>
            <HeroCalendar.Heading />
            <HeroCalendar.NavButton slot="next">
              <ChevronRight className="h-4 w-4" />
            </HeroCalendar.NavButton>
          </HeroCalendar.Header>

          <HeroCalendar.Grid>
            <HeroCalendar.GridHeader>
              {(day) => <HeroCalendar.HeaderCell>{day}</HeroCalendar.HeaderCell>}
            </HeroCalendar.GridHeader>
            <HeroCalendar.GridBody>
              {(date) => <HeroCalendar.Cell date={date} />}
            </HeroCalendar.GridBody>
          </HeroCalendar.Grid>
        </HeroCalendar>
      </HeroDatePicker.Popover>

      {description && !hasError && (
        <Description className={cn('text-xs text-muted-foreground', classNames?.description)}>
          {description}
        </Description>
      )}
      {hasError && errorMessage && (
        <FieldError className={cn('text-xs text-destructive', classNames?.error)}>
          {errorMessage}
        </FieldError>
      )}
    </HeroDatePicker>
  );
}
